import React, { useState, useEffect } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    addMonths,
    subMonths,
    addWeeks,
    subWeeks,
    isSameMonth,
    isSameDay,
    subDays,
    setMonth,
    setYear,
    getMonth,
    getYear,
    startOfDay,
    endOfDay
} from 'date-fns';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Grid,
    CircularProgress,
    Tooltip,
    Stack,
    Button,
    Popover,
    Select,
    MenuItem,
    FormControl,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import { ChevronLeft, ChevronRight, ArrowDropDown, LocationOn, AccessTime } from '@mui/icons-material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { queryGoogleCalendar } from '../api/actions/query';
import EventDetailsPopup from './EventDetailsPopup';

const GridView = ({ settings }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [viewType, setViewType] = useState('month'); // month, week, day, schedule

    // User Role Scoping for Grid View
    const loggedInUserStr = localStorage.getItem("user");
    const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
    const userRole = (loggedInUser?.roleDetails?.name || loggedInUser?.role || "").toLowerCase();
    const isSuperOrStoreAdmin = userRole === "super admin" || 
                                userRole === "store admin" || 
                                userRole === "admin" || 
                                userRole === "super_admin" || 
                                userRole === "store_admin";
    const userEmail = (loggedInUser?.email || "").toLowerCase();
    const userId = (loggedInUser?._id || loggedInUser?.id || "").toString();

    const allConsultationTypes = settings?.consultationTypes || settings?.googleConfig?.consultationTypes || [];
    const defaultName = (settings?.calendarProvider === 'outlook' ? settings?.outlookConfig?.name : settings?.googleConfig?.name) ||
        settings?.defaultConsultationType?.name ||
        settings?.googleConfig?.name ||
        settings?.outlookConfig?.name ||
        settings?.name ||
        "General Consultation (Default)";

    // Visible consultation types for logged-in user (and active)
    const visibleConsultationTypes = allConsultationTypes.filter(type => {
        if (type.isActive === false) return false;
        if (isSuperOrStoreAdmin) return true;
        const managerEmail = (type.assignedManagerEmail || "").toLowerCase();
        const managerId = (type.assignedManagerId || "").toString();
        const managerRole = (type.assignedManagerRole || "").toLowerCase();
        const calEmail = (type.calendarEmail || "").toLowerCase();

        return (userEmail && (managerEmail === userEmail || calEmail === userEmail)) ||
               (userId && managerId === userId) ||
               (userRole && managerRole === userRole);
    });

    // Default filter for dropdown:
    // If Super Admin / Store Admin: 'all'
    // If Developer / Restricted Manager: first assigned consultation type ID
    const initialFilter = React.useMemo(() => {
        if (isSuperOrStoreAdmin) return 'all';
        if (visibleConsultationTypes.length > 0 && visibleConsultationTypes[0]._id) {
            return visibleConsultationTypes[0]._id;
        }
        return 'all';
    }, [isSuperOrStoreAdmin, visibleConsultationTypes]);

    const [selectedConsultationFilter, setSelectedConsultationFilter] = useState(initialFilter);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            let startDate, endDate;

            if (viewType === 'month' || viewType === 'schedule') {
                const monthStart = startOfMonth(currentDate);
                const monthEnd = endOfMonth(currentDate);
                startDate = subDays(startOfWeek(monthStart), 7);
                endDate = addDays(endOfWeek(monthEnd), 7);
            } else if (viewType === 'week') {
                startDate = startOfWeek(currentDate);
                endDate = endOfWeek(currentDate);
            } else if (viewType === 'day') {
                startDate = startOfDay(currentDate);
                endDate = endOfDay(currentDate);
            }

            const filterId = selectedConsultationFilter === 'all' ? null : selectedConsultationFilter;
            const response = await queryGoogleCalendar(startDate.toISOString(), endDate.toISOString(), filterId);
            if (response && response.data) {
                setEvents(response.data);
            }
        } catch (error) {
            console.error("Error fetching events for grid:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [currentDate, viewType, selectedConsultationFilter]);

    const handleNext = () => {
        if (viewType === 'month' || viewType === 'schedule') {
            setCurrentDate(addMonths(currentDate, 1));
        } else if (viewType === 'week') {
            setCurrentDate(addWeeks(currentDate, 1));
        } else if (viewType === 'day') {
            setCurrentDate(addDays(currentDate, 1));
        }
    };

    const handlePrev = () => {
        if (viewType === 'month' || viewType === 'schedule') {
            setCurrentDate(subMonths(currentDate, 1));
        } else if (viewType === 'week') {
            setCurrentDate(subWeeks(currentDate, 1));
        } else if (viewType === 'day') {
            setCurrentDate(subDays(currentDate, 1));
        }
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleHeaderClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleHeaderClose = () => {
        setAnchorEl(null);
    };

    const handleDateSelect = (date) => {
        setCurrentDate(date);
        handleHeaderClose();
    };

    const handleEventClick = (event) => {
        setSelectedEvent(event);
    };

    const handleClosePopup = () => {
        setSelectedEvent(null);
    };

    const getHeaderText = () => {
        if (viewType === 'day') {
            return format(currentDate, 'MMMM d, yyyy');
        } else if (viewType === 'week') {
            const start = startOfWeek(currentDate);
            const end = endOfWeek(currentDate);
            if (isSameMonth(start, end)) {
                return `${format(start, 'MMMM yyyy')}`;
            }
            return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
        }
        return format(currentDate, 'MMMM yyyy');
    };

    const renderHeader = () => {
        const open = Boolean(anchorEl);
        const id = open ? 'simple-popover' : undefined;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" size="small" onClick={goToToday} sx={{ color: '#0d3559', borderColor: '#e0e0e0', textTransform: 'none' }}>
                        Today
                    </Button>

                    <Box>
                        <IconButton onClick={handlePrev} size="small">
                            <ChevronLeft />
                        </IconButton>
                        <IconButton onClick={handleNext} size="small">
                            <ChevronRight />
                        </IconButton>
                    </Box>

                    <Button
                        endIcon={<ArrowDropDown />}
                        onClick={handleHeaderClick}
                        sx={{ color: '#0d3559', fontWeight: 'bold', textTransform: 'none', fontSize: '1.25rem' }}
                    >
                        {getHeaderText()}
                    </Button>

                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleHeaderClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <Box sx={{ p: 1, '& .react-datepicker': { border: 'none' }, '& .react-datepicker__header': { bgcolor: 'white' } }}>
                            <DatePicker
                                selected={currentDate}
                                onChange={handleDateSelect}
                                inline
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={10}
                            />
                        </Box>
                    </Popover>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    {/* Consultation Type Filter Dropdown */}
                    {(isSuperOrStoreAdmin || visibleConsultationTypes.length > 1) && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                                value={selectedConsultationFilter}
                                onChange={(e) => setSelectedConsultationFilter(e.target.value)}
                                sx={{ bgcolor: 'background.paper', height: 36, fontSize: '0.875rem' }}
                            >
                                {isSuperOrStoreAdmin && <MenuItem value="all">All Consultation Types</MenuItem>}
                                {isSuperOrStoreAdmin && <MenuItem value="general">{defaultName}</MenuItem>}
                                {visibleConsultationTypes.map(type => (
                                    <MenuItem key={type._id || type.name} value={type._id}>
                                        {type.name} ({type.assignedManagerName || type.assignedManagerEmail || 'Unassigned'})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Select
                        value={viewType}
                        onChange={(e) => setViewType(e.target.value)}
                        size="small"
                        sx={{ minWidth: 100, height: 36, bgcolor: 'background.paper' }}
                    >
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="day">Day</MenuItem>
                        <MenuItem value="schedule">Schedule</MenuItem>
                    </Select>
                </Stack>
            </Box>
        );
    };

    // --- VIEW RENDERERS ---

    // Helper to format time in settings timezone
    const formatEventTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        const timeZone = settings?.timeZone || 'UTC';
        
        try {
            return date.toLocaleTimeString('en-US', {
                timeZone: timeZone,
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting time:', error);
            return format(date, 'p');
        }
    };

    const renderEventBox = (event, idx) => {
        const eventTime = event.start?.dateTime ? formatEventTime(event.start.dateTime) : '';
        
        return (
            <Tooltip key={event.id || idx} title={`${event.summary} (${eventTime})`}>
                <Box
                    onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                    sx={{
                        bgcolor: '#e3f2fd',
                        color: '#0d47a1',
                        p: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        mb: 0.5,
                        '&:hover': { bgcolor: '#bbdefb' }
                    }}
                >
                    {eventTime && `${eventTime} `}{event.summary}
                </Box>
            </Tooltip>
        );
    };

    const renderMonthView = () => {
        const dateFormat = "EEEE";
        const days = [];
        let startDate = startOfWeek(startOfMonth(currentDate));

        // Header Row
        for (let i = 0; i < 7; i++) {
            days.push(
                <Grid item xs key={`header-${i}`} sx={{ textAlign: 'center', fontWeight: 'bold', color: '#666', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    {format(addDays(startDate, i), dateFormat)}
                </Grid>
            );
        }

        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(monthStart);
        const start = startOfWeek(monthStart);
        const end = endOfWeek(monthEnd);

        const rows = [];
        let day = start;
        let dayCells = [];

        while (day <= end) {
            for (let i = 0; i < 7; i++) {
                const cloneDay = day;
                const dateNum = format(day, "d");

                const dayEvents = events.filter(event => {
                    const eventDate = new Date(event.start?.dateTime || event.start?.date);
                    return isSameDay(eventDate, cloneDay);
                });

                dayCells.push(
                    <Grid item xs key={day.toString()} sx={{ height: '150px', border: '1px solid #e0e0e0', p: 1, bgcolor: !isSameMonth(day, monthStart) ? '#f9f9f9' : 'white', overflow: 'hidden' }}>
                        <Typography
                            variant="body2"
                            onClick={() => { setCurrentDate(cloneDay); setViewType('day'); }}
                            sx={{
                                float: 'right',
                                fontWeight: isSameDay(day, new Date()) ? 'bold' : 'normal',
                                color: isSameDay(day, new Date()) ? '#1976d2' : '#333',
                                cursor: 'pointer',
                                '&:hover': { color: '#1976d2', textDecoration: 'underline' }
                            }}
                        >
                            {dateNum}
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            {dayEvents.slice(0, 3).map((e, idx) => renderEventBox(e, idx))}
                            {dayEvents.length > 3 && (
                                <Typography
                                    variant="caption"
                                    onClick={() => { setCurrentDate(cloneDay); setViewType('day'); }}
                                    sx={{
                                        color: '#666',
                                        textAlign: 'center',
                                        display: 'block',
                                        cursor: 'pointer',
                                        mt: 0.5,
                                        '&:hover': { color: '#1976d2', fontWeight: 'bold' }
                                    }}
                                >
                                    + {dayEvents.length - 3} more
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                );
                day = addDays(day, 1);
            }
            rows.push(<Grid container key={day.toString()}>{dayCells}</Grid>);
            dayCells = [];
        }

        return (
            <>
                <Grid container>{days}</Grid>
                <Box>{rows}</Box>
            </>
        );
    };

    const renderWeekView = () => {
        const start = startOfWeek(currentDate);
        const days = [];

        for (let i = 0; i < 7; i++) {
            const day = addDays(start, i);
            const dateNum = format(day, "EEE d");

            const dayEvents = events.filter(event => {
                const eventDate = new Date(event.start?.dateTime || event.start?.date);
                return isSameDay(eventDate, day);
            });

            days.push(
                <Grid item xs key={day.toString()} sx={{ height: 'calc(100vh - 250px)', overflowY: 'auto', border: '1px solid #e0e0e0', borderTop: 'none', p: 1, bgcolor: 'white' }}>
                    <Typography align="center" variant="subtitle2" sx={{ mb: 2, fontWeight: isSameDay(day, new Date()) ? 'bold' : 'normal', color: isSameDay(day, new Date()) ? '#1976d2' : '#333', borderBottom: '1px solid #eee', pb: 1 }}>
                        {dateNum}
                    </Typography>
                    <Box>
                        {dayEvents.map((e, idx) => renderEventBox(e, idx))}
                    </Box>
                </Grid>
            );
        }

        return <Grid container sx={{ height: '100%' }}>{days}</Grid>;
    };

    const renderDayView = () => {
        const dayEvents = events.filter(event => {
            const eventDate = new Date(event.start?.dateTime || event.start?.date);
            return isSameDay(eventDate, currentDate);
        });

        // Simple Day View (List of events for the day)
        // Ideally this would be an hourly grid, but a list is a good start
        const hours = Array.from({ length: 24 }, (_, i) => i);

        return (
            <Box sx={{ height: '100%', overflowY: 'auto', border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                    {format(currentDate, 'EEEE, MMMM d')}
                </Typography>
                <Box sx={{ p: 2 }}>
                    {dayEvents.length === 0 ? (
                        <Typography color="text.secondary">No events for this day.</Typography>
                    ) : (
                        dayEvents.map((e, idx) => {
                            return (
                                <Paper key={e.id} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #1976d2', cursor: 'pointer' }} onClick={() => handleEventClick(e)}>
                                    <Typography variant="subtitle1" fontWeight="bold">{e.summary}</Typography>
                                    <Stack direction="row" spacing={2} sx={{ mt: 1, color: 'text.secondary' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography variant="body2">
                                                {formatEventTime(e.start?.dateTime)} - {formatEventTime(e.end?.dateTime)}
                                            </Typography>
                                        </Box>
                                        {e.location && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                                                <Typography variant="body2">{e.location}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            );
                        })
                    )}
                </Box>
            </Box>
        );
    };

    const renderScheduleView = () => {
        // Group events by day
        const groupedEvents = events.reduce((acc, event) => {
            const dateKey = format(new Date(event.start?.dateTime || event.start?.date), 'yyyy-MM-dd');
            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push(event);
            return acc;
        }, {});

        // Sort dates
        const sortedDates = Object.keys(groupedEvents).sort();

        return (
            <Box sx={{ height: '100%', overflowY: 'auto' }}>
                <List>
                    {sortedDates.map(dateKey => (
                        <Box key={dateKey}>
                            <Box sx={{ bgcolor: '#f5f5f5', px: 2, py: 1, fontWeight: 'bold' }}>
                                {format(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
                            </Box>
                            {groupedEvents[dateKey].map(event => (
                                <React.Fragment key={event.id}>
                                    <ListItem button onClick={() => handleEventClick(event)}>
                                        <ListItemText
                                            primary={event.summary}
                                            secondary={
                                                <Stack direction="row" spacing={1} component="span">
                                                    <Typography variant="caption" component="span">
                                                        {formatEventTime(event.start?.dateTime)} - {formatEventTime(event.end?.dateTime)}
                                                    </Typography>
                                                    {event.location && (
                                                        <Typography variant="caption" component="span"> | {event.location}</Typography>
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </Box>
                    ))}
                    {sortedDates.length === 0 && (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No events scheduled for this month.</Typography>
                        </Box>
                    )}
                </List>
            </Box>
        );
    }

    if (loading && events.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {renderHeader()}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {viewType === 'month' && renderMonthView()}
                {viewType === 'week' && renderWeekView()}
                {viewType === 'day' && renderDayView()}
                {viewType === 'schedule' && renderScheduleView()}
            </Box>
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', borderTop: '1px solid #eee', mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                    Timezone:
                </Typography>
                <Typography variant="caption" sx={{ ml: 1, color: '#333' }}>
                    {settings?.timeZone || 'UTC'}
                </Typography>
            </Box>
            {selectedEvent && (
                <EventDetailsPopup
                    event={selectedEvent}
                    onClose={handleClosePopup}
                />
            )}
        </Paper>
    );
};

export default GridView;